import { useState, type FormEvent } from 'react';
import { color } from '../styles/theme';
import { useRedeemReset, passwordProblems, problemsFrom } from '../api/account';
import { ApiError } from '../api/client';
import { Button, inputStyle } from './ui';
import { IconSpinner } from './icons';
import { AuthShell, PasswordChecklist } from './AuthShell';

/**
 * Invite acceptance and password reset — the target of the link the API emails.
 *
 * One screen for both: the API issues the same kind of token either way and
 * tells us which it was in `wasInvite`, so the only difference is the wording.
 */
export function SetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const redeem = useRedeemReset();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState<{ wasInvite: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const local = passwordProblems(password);
  const serverProblems = problemsFrom(redeem.error);
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length > 0 && local.length === 0 && !mismatch && confirm.length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await redeem.mutateAsync({ token, password });
      setDone({ wasInvite: res.wasInvite });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not set your password. Try again.',
      );
    }
  }

  if (done) {
    return (
      <AuthShell
        title="ENACT Lab"
        sub={done.wasInvite ? 'Your account is ready' : 'Password updated'}
      >
        <p style={{ fontSize: 13.5, color: color.ink, margin: 0, lineHeight: 1.6 }}>
          {done.wasInvite
            ? 'Your password is set. Sign in to reach the dashboard.'
            : 'Your password has been changed. Any other devices signed in as you have been signed out.'}
        </p>
        <Button onClick={onDone} style={{ justifyContent: 'center' }}>
          Go to sign in
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="ENACT Lab"
      sub="Choose a password"
      onSubmit={submit}
      footnote="This link can be used once and expires."
    >
      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>New password</span>
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          required
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
      </label>

      <PasswordChecklist
        problems={password.length > 0 ? local : serverProblems}
        dirty={password.length > 0}
      />

      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>Confirm password</span>
        <input
          type="password"
          value={confirm}
          autoComplete="new-password"
          required
          onChange={(e) => setConfirm(e.target.value)}
          style={inputStyle}
        />
      </label>

      {mismatch && (
        <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>
          Those two passwords do not match.
        </p>
      )}
      {error && (
        <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>
      )}

      <Button
        type="submit"
        disabled={!ready || redeem.isPending}
        style={{ justifyContent: 'center', marginTop: 4 }}
      >
        {redeem.isPending && <IconSpinner size={15} />}
        {redeem.isPending ? 'Saving…' : 'Set password'}
      </Button>
    </AuthShell>
  );
}
